// What a change event on ONE of the code boxes does to all of them. Kept import-free so it
// unit-tests without the hook's dependency graph.
//
// The boxes carry NO `maxLength`, because iOS Safari and Chrome on Android both CLIP an
// autofilled value to it: with one character allowed, the SMS code suggestion delivered only its
// first digit and the code never completed, and any fixed bound still clips a formatted code
// ("G-123456", "123 456"). So a box can receive anything, and this decides what it means:
//
// - what was INSERTED is told apart from the digit the box already held, using the caret: the
//   browser inserts at the caret, so text before it is new when the old digit is after it, and
//   vice versa. A value that is exactly a code is a replacement, wherever the caret is;
// - a whole code (the SMS autofill) fills every box, starting from the first;
// - a digit typed into a box replaces what it held, as a one-character box would;
// - a keystroke with no digit in it changes nothing, and does not move focus;
// - a shorter run (part of a code) spreads forward from the box it went into.
//
// `caret` is the input's `selectionStart` after the change; when unknown, the insertion is
// taken to be after the old digit.
export function applyDigitInput(
  prev: readonly string[],
  i: number,
  raw: string,
  caret?: number | null,
): { digits: string[]; focus: number | null } {
  const length = prev.length;
  const digitsOf = (s: string) => s.replace(/\D/g, '');
  const next = [...prev];
  const old = prev[i];

  let inserted = raw;
  const keptOld = !!old && raw.length > old.length && digitsOf(raw).length !== length;
  if (keptOld) {
    inserted = caret != null && caret < raw.length ? raw.slice(0, caret) : raw.slice(old.length);
  }
  const cleaned = digitsOf(inserted);

  if (cleaned.length >= length) {
    for (let n = 0; n < length; n++) next[n] = cleaned[n];
    return { digits: next, focus: length - 1 };
  }

  if (!cleaned) {
    if (keptOld) return { digits: next, focus: null };
    next[i] = '';
    return { digits: next, focus: null };
  }

  const run = cleaned.slice(0, length - i);
  for (let n = 0; n < run.length; n++) next[i + n] = run[n];
  return { digits: next, focus: Math.min(i + run.length, length - 1) };
}
